import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Post, PostType } from '../types';
import ConfirmDialog from './ConfirmDialog';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

interface FeedViewProps {
  posts: Post[];
  onLike: (id: string) => void;
  isAuthenticated: boolean;
  localLikes: string[];
  onLoginRequest: () => void;
  onDeletePost: (id: string) => Promise<boolean>;
  onUpdatePost: (id: string, updates: Partial<Post>) => Promise<boolean>;
}

const SovereignBadge: React.FC = () => (
  <div className="flex items-center gap-1 ml-1.5 px-1.5 py-0.5 bg-indigo-600 text-white border border-white/10 rounded-full animate-in zoom-in-90 duration-300 shadow-sm">
    <svg width="8" height="8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 19.5V21H12.5L19.5 14L18 12.5L11 19.5ZM21.5 12L20 10.5L21 9.5C21.3 9.2 21.7 9.2 22 9.5L22.5 10C22.8 10.3 22.8 10.7 22.5 11L21.5 12Z" fill="white" />
    </svg>
    <span className="text-[8px] font-black uppercase tracking-wider text-white/95 leading-none">YourSite</span>
  </div>
);

const NsfwOverlay: React.FC<{ onReveal: () => void }> = ({ onReveal }) => (
  <div className="absolute inset-0 z-10 bg-slate-900/80 backdrop-blur-3xl flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-300">
    <div className="w-16 h-16 rounded-full bg-rose-500/20 text-rose-500 flex items-center justify-center mb-4 border border-rose-500/30">
      <i className="fa-solid fa-eye-slash text-2xl"></i>
    </div>
    <h4 className="text-white font-bold text-lg mb-1 tracking-tight">Sensitive Content</h4>
    <p className="text-gray-400 text-sm mb-6 max-w-[240px]">This post contains material that may be sensitive for some viewers.</p>
    <button 
      onClick={onReveal}
      className="px-6 py-2.5 bg-white text-slate-900 rounded-full font-bold text-sm shadow-xl hover:scale-105 active:scale-95 transition-all"
    >
      Show Content
    </button>
  </div>
);

const LoadingIndicator: React.FC = () => (
  <div className="py-12 flex flex-col items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <div className="flex gap-1.5">
      <div className="w-2 h-2 rounded-full bg-indigo-600 animate-bounce [animation-delay:-0.3s]"></div>
      <div className="w-2 h-2 rounded-full bg-indigo-600 animate-bounce [animation-delay:-0.15s]"></div>
      <div className="w-2 h-2 rounded-full bg-indigo-600 animate-bounce"></div>
    </div>
    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Gathering more stories</span>
  </div>
);

const FeedView: React.FC<FeedViewProps> = ({ 
  posts, onLike, isAuthenticated, localLikes, onLoginRequest, onDeletePost, onUpdatePost 
}) => {
  const [filter, setFilter] = useState<PostType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [revealedNsfw, setRevealedNsfw] = useState<Set<string>>(new Set());
  const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);

  // Infinite Scroll States
  const INITIAL_POST_COUNT = 25;
  const LOAD_MORE_COUNT = 15;
  const [visibleCount, setVisibleCount] = useState(INITIAL_POST_COUNT);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const filteredPosts = useMemo(() => {
    return posts.filter(p => {
      const matchesType = filter === 'all' || p.type === filter;
      const s = search.toLowerCase();
      return matchesType && (p.content.toLowerCase().includes(s) || p.author.toLowerCase().includes(s));
    });
  }, [posts, filter, search]);

  const visiblePosts = useMemo(() => {
    return filteredPosts.slice(0, visibleCount);
  }, [filteredPosts, visibleCount]);

  useEffect(() => {
    setVisibleCount(INITIAL_POST_COUNT);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filter, search]);

  // Handle Escape key to close Lightbox
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedImageUrl(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && visibleCount < filteredPosts.length) {
      setIsRefreshing(true);
      setTimeout(() => {
        setVisibleCount(prev => prev + LOAD_MORE_COUNT);
        setIsRefreshing(false);
      }, 600);
    }
  }, [visibleCount, filteredPosts.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    });
    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleMediaError = (e: any, url: string | undefined) => {
    console.error(`[FeedView] Failed to load media: ${url}`, e);
  };

  const startEditing = (post: Post) => {
    setEditingId(post.id);
    setEditContent(post.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async (id: string) => {
    setIsSaving(true);
    const success = await onUpdatePost(id, { content: editContent });
    if (success) {
      setEditingId(null);
      setEditContent('');
    }
    setIsSaving(false);
  };

  const revealNsfw = (id: string) => {
    setRevealedNsfw(prev => new Set([...prev, id]));
  };

  const hideNsfw = (id: string) => {
    setRevealedNsfw(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const renderMarkdown = (content: string) => {
    const rawHtml = marked.parse(content, { breaks: true, gfm: true }) as string;
    return { __html: DOMPurify.sanitize(rawHtml) };
  };

  return (
    <div className="max-w-2xl mx-auto">
      <ConfirmDialog 
        isOpen={!!isDeletingId}
        title="Delete this post?"
        message="This action is permanent and will remove all associated media."
        confirmLabel="Delete"
        variant="danger"
        onClose={() => setIsDeletingId(null)}
        onConfirm={async () => {
          if (isDeletingId) {
            await onDeletePost(isDeletingId);
            setIsDeletingId(null);
          }
        }}
      />

      {/* Image Lightbox / Expand Modal */}
      {expandedImageUrl && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-xl animate-in fade-in duration-300 overflow-hidden cursor-zoom-out"
          onClick={() => setExpandedImageUrl(null)}
        >
          <button 
            className="absolute top-8 right-8 z-[110] w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all border border-white/10"
            onClick={(e) => { e.stopPropagation(); setExpandedImageUrl(null); }}
          >
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
          <img 
            src={expandedImageUrl} 
            className="max-h-[95vh] max-w-[95vw] rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 object-contain cursor-default"
            alt="Expanded view"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-[64px] z-40 bg-gray-50 dark:bg-slate-950 -mx-4 px-4 py-4 mb-6 border-b border-gray-100 dark:border-gray-800 flex flex-col gap-4 shadow-sm transition-colors duration-300">
        <div className="flex flex-wrap gap-2">
          {['all', PostType.SHORT, PostType.LONG, PostType.IMAGE, PostType.VIDEO, PostType.AUDIO].map(type => (
            <button key={type} onClick={() => setFilter(type as any)} className={`px-4 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${filter === type ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white dark:bg-slate-900 text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-indigo-300'}`}>
              {type}
            </button>
          ))}
        </div>
        
        <div className="relative group">
          <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm group-focus-within:text-indigo-500 transition-colors"></i>
          <input 
            type="text" 
            placeholder="Search feed..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white dark:bg-slate-900 border border-gray-100 dark:border-gray-800 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm"
          />
        </div>
      </div>

      <div className="space-y-6">
        {visiblePosts.map(post => {
          const isLocalOwner = !!isAuthenticated && !post.sourceUrl;
          const isEditing = editingId === post.id;
          const isRevealed = revealedNsfw.has(post.id);
          const needsCensorship = !!post.isNsfw && !isRevealed;
          
          // Solid heart if either the owner liked it (persistent) OR this browser liked it (local)
          const hasLikedGlobally = !!post.isLikedByOwner;
          const hasLikedLocally = localLikes.includes(post.id);
          const isHeartSolid = hasLikedGlobally || hasLikedLocally;

          return (
            <div key={post.id} className="group bg-white dark:bg-slate-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden hover:shadow-xl transition-all animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-50 dark:bg-slate-800 border border-indigo-100 dark:border-slate-700 flex items-center justify-center overflow-hidden">
                      {post.authorAvatar ? (
                        <img 
                          src={post.authorAvatar} 
                          className="w-full h-full object-cover" 
                          alt="" 
                          onError={(e) => handleMediaError(e, post.authorAvatar)}
                        />
                      ) : (
                        <i className="fa-solid fa-user text-indigo-200"></i>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold">{post.author}</h3>
                        {!!post.isNsfw && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-rose-500 text-white font-black rounded-md tracking-widest leading-none">NSFW</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                        {new Date(post.createdAt).toLocaleDateString()}
                        {!!post.sourceLabel && (
                          <span className="flex items-center gap-1 opacity-80">
                            • <i className={`fa-solid ${!!post.isYourSite ? 'fa-link' : 'fa-rss'} text-[10px]`}></i> {post.sourceLabel}
                          </span>
                        )}
                        {!!post.isYourSite && <SovereignBadge />}
                      </div>
                    </div>
                  </div>
                  
                  {isLocalOwner && (
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => startEditing(post)} 
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-800 transition-all"
                        title="Edit Post"
                      >
                        <i className="fa-solid fa-pen text-xs"></i>
                      </button>
                      <button 
                        onClick={() => setIsDeletingId(post.id)} 
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-slate-800 transition-all"
                        title="Delete Post"
                      >
                        <i className="fa-solid fa-trash-can text-xs"></i>
                      </button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="mb-6 space-y-3">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full p-4 bg-gray-50 dark:bg-slate-800 rounded-2xl border-2 border-indigo-100 dark:border-slate-700 focus:border-indigo-500 outline-none text-gray-700 dark:text-gray-200 min-h-[120px] transition-all"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <button onClick={cancelEditing} className="px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all">Cancel</button>
                      <button onClick={() => saveEdit(post.id)} disabled={isSaving} className="px-4 py-2 text-sm font-bold bg-indigo-600 text-white rounded-xl shadow-md flex items-center gap-2">
                        {isSaving && <i className="fa-solid fa-spinner animate-spin"></i>} Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="markdown-content text-gray-700 dark:text-gray-300 mb-6" dangerouslySetInnerHTML={renderMarkdown(post.content)} />
                )}

                {post.mediaUrl && !isEditing && (
                  <div className="relative mb-6 rounded-2xl overflow-hidden bg-black/5 dark:bg-white/5 flex items-center justify-center min-h-[100px] border border-gray-50 dark:border-gray-800">
                    {needsCensorship && <NsfwOverlay onReveal={() => revealNsfw(post.id)} />}
                    <div className={`w-full transition-all duration-700 ${needsCensorship ? 'blur-[40px] grayscale' : ''}`}>
                      {post.type === PostType.IMAGE && (
                        <img 
                          src={post.mediaUrl} 
                          className={`w-full h-auto block ${!needsCensorship ? 'cursor-zoom-in hover:scale-[1.01] active:scale-[0.99]' : ''} transition-all duration-300`} 
                          alt="" 
                          onClick={() => !needsCensorship && setExpandedImageUrl(post.mediaUrl || null)}
                          onError={(e) => handleMediaError(e, post.mediaUrl)}
                        />
                      )}
                      {post.type === PostType.VIDEO && <video src={post.mediaUrl} controls={!needsCensorship} className="w-full" onError={(e) => handleMediaError(e, post.mediaUrl)}/>}
                      {post.type === PostType.AUDIO && <audio src={post.mediaUrl} controls={!needsCensorship} className="w-full px-4 py-2" onError={(e) => handleMediaError(e, post.mediaUrl)}/>}
                    </div>
                    {!!(!!post.isNsfw && isRevealed) && (
                      <button onClick={() => hideNsfw(post.id)} className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-md hover:bg-black/60 transition-all"><i className="fa-solid fa-eye text-xs"></i></button>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => onLike(post.id)} 
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${isHeartSolid ? 'bg-rose-50 text-rose-600 dark:bg-rose-900/20' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                  >
                    <i className={`${isHeartSolid ? 'fa-solid' : 'fa-regular'} fa-heart`}></i>
                    <span className="font-bold text-sm">{post.likeCount}</span>
                    {hasLikedGlobally && !hasLikedLocally && !isAuthenticated && (
                      <span className="text-[10px] font-black uppercase tracking-tighter opacity-50 ml-1">Owner</span>
                    )}
                  </button>
                  {post.tags.length > 0 && (
                    <div className="flex gap-1 ml-auto overflow-x-auto no-scrollbar">
                      {post.tags.map(tag => (
                        <span key={tag} className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider px-2 py-1 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg">#{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div ref={observerTarget} className="min-h-[100px] w-full mt-6">
        {isRefreshing && <LoadingIndicator />}
        {!isRefreshing && visibleCount >= filteredPosts.length && filteredPosts.length > INITIAL_POST_COUNT && (
          <div className="py-12 flex flex-col items-center justify-center text-gray-400 gap-4 animate-in fade-in duration-700">
            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-900 flex items-center justify-center"><i className="fa-solid fa-check text-xs"></i></div>
            <p className="text-[10px] font-bold uppercase tracking-widest">End of the world</p>
          </div>
        )}
        {filteredPosts.length === 0 && (
          <div className="py-24 text-center">
            <i className="fa-solid fa-ghost text-4xl text-gray-200 mb-4"></i>
            <h3 className="text-xl font-bold mb-2">Nothing found</h3>
            <p className="text-gray-400 text-sm">Adjust filters or search terms.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedView;